import { MutableRefObject, KeyboardEvent } from 'react';

import { Keys } from '../types/keyboard';

export const handleTableKeydown =
  (
    tableRef: MutableRefObject<HTMLDivElement>,
    firstColumnIndex: number,
    lastColumnIndex: number,
    lastRowIndex: number,
    hasActions: boolean,
    onColumnSort: (key: string) => boolean,
  ) =>
  (event: KeyboardEvent<HTMLDivElement>) => {
    let element = document.activeElement as HTMLElement | null;

    if (!tableRef.current.contains(element)) return;

    let activeColumnIndex = findDataPropertyNumberValue('data-col', element);
    let activeRowIndex = findDataPropertyNumberValue('data-row', element);
    let actualLastColumnIndex =
      activeRowIndex === 0 ? lastColumnIndex : lastColumnIndex - (hasActions ? 0 : 1);

    switch (event.key) {
      case Keys.Space:
        if (activeRowIndex === 0) {
          // Sort the column header
          const handled = onColumnSort(element?.getAttribute('data-key') as string);
          handled && event.preventDefault();
        }

        if (event.shiftKey) {
          // shift + space selects the current row if selection is allowed.
          const handled = findCheckboxAndClick(element);
          handled && event.preventDefault();
        }
        break;
      case Keys.Enter:
        if (activeRowIndex === 0) {
          // Sort the column header
          const handled = onColumnSort(element?.getAttribute('data-key') as string);
          handled && event.preventDefault();
        }
        break;
      case Keys.PageDown: {
        event.preventDefault();
        // Move down 5 rows or to the bottom row if distance is less than 5.
        let toRowIndex = Math.min(activeRowIndex + 5, lastRowIndex);
        doFocusSwap(tableRef.current, element, toRowIndex, activeColumnIndex);
        break;
      }

      case Keys.PageUp: {
        event.preventDefault();
        // Move up 5 rows or to the top row if distance is less than 5.
        let toRowIndex = Math.max(activeRowIndex - 5, 0);
        doFocusSwap(tableRef.current, element, toRowIndex, activeColumnIndex);
        break;
      }

      case Keys.Home: {
        event.preventDefault();
        // Go to the first element in the row.
        const newRowIndex = event.ctrlKey ? 0 : activeRowIndex;
        doFocusSwap(tableRef.current, element, newRowIndex, firstColumnIndex);
        break;
      }

      case Keys.End: {
        event.preventDefault();
        // Go to the last element in the row.
        const newRowIndex = event.ctrlKey ? lastRowIndex : activeRowIndex;
        doFocusSwap(tableRef.current, element, newRowIndex, actualLastColumnIndex);
        break;
      }

      case Keys.ArrowUp: {
        event.preventDefault();
        // Move up one row. If at the top row, don't do anything.
        let toRowIndex = Math.max(activeRowIndex - 1, 0);
        const newElement = doFocusSwap(tableRef.current, element, toRowIndex, activeColumnIndex);

        if (event.shiftKey && newElement) {
          const handled = findCheckboxAndClick(newElement);
          handled && event.preventDefault();
        }
        break;
      }

      case Keys.ArrowDown: {
        event.preventDefault();
        // Move down one row. If at the last row, don't do anything.
        let toRowIndex = Math.min(activeRowIndex + 1, lastRowIndex);
        const newElement = doFocusSwap(tableRef.current, element, toRowIndex, activeColumnIndex);

        if (event.shiftKey && newElement) {
          const handled = findCheckboxAndClick(newElement);
          handled && event.preventDefault();
        }
        break;
      }

      case Keys.ArrowLeft: {
        event.preventDefault();
        // Move left one column. If at the first column, don't do anything.
        let toColIndex = Math.max(firstColumnIndex, activeColumnIndex - 1);
        doFocusSwap(tableRef.current, element, activeRowIndex, toColIndex);
        break;
      }

      case Keys.ArrowRight: {
        event.preventDefault();
        // Move right one column. If at the last column, don't do anything.
        let toColIndex = Math.min(actualLastColumnIndex, activeColumnIndex + 1);
        doFocusSwap(tableRef.current, element, activeRowIndex, toColIndex);
        break;
      }
    }
  };

function findCheckboxAndClick(element: HTMLElement | null) {
  const checkbox = element?.parentElement
    ?.querySelector('[data-col="0"]')
    ?.querySelector('button, input') as HTMLElement | null;
  if (checkbox) {
    checkbox.click();
    return true;
  }
  return false;
}

export function findDataPropertyNumberValue(property: string, element: HTMLElement | null): number {
  if (!element) return -1;

  const value = element.getAttribute(property);
  if (value) return Number(value);

  return findDataPropertyNumberValue(property, element.parentElement);
}

export function resetTabIndex(element: HTMLElement | null) {
  if (!element) return;

  if (['button', 'input'].includes(element.nodeName.toLowerCase())) {
    // Buttons and checkboxes are tabbable even if we remove the tab index
    // so we set it to -1 to take them out of the current tab order
    element.setAttribute('tabindex', '-1');
  } else {
    // However if we set tabindex -1 on elements that are not buttons,
    // they become focusable by clicking on them, which just looks wrong.
    // So for non-buttons, we remove the tabindex completely.
    element.removeAttribute('tabindex');
  }
}

export function focusColumn(column: HTMLElement | null) {
  if (!column) return;

  // Check whether the column contains a focusable element
  // like a menu-button or checkbox-input, in that case we want to tab
  // to that child element and not the column itself.
  const element = column.querySelector('button, input') as HTMLElement | null;
  if (element) {
    element.setAttribute('tabindex', '0');
    element.focus();
    return;
  }

  // No focusable child found, lets focus the column itself.
  column.setAttribute('tabindex', '0');
  column.focus();
}

function doFocusSwap(
  table: HTMLDivElement,
  element: HTMLElement | null,
  newRowIndex: number,
  newColumnIndex: number,
) {
  let domRow = table.querySelector(`[data-row="${newRowIndex}"]`);
  let column = domRow?.querySelector(`[data-col="${newColumnIndex}"]`) as HTMLElement | null;
  resetTabIndex(element);
  focusColumn(column);

  column?.scrollIntoView({ block: 'nearest' });
  return column;
}
